/**
 * Builds the HTML for a Nota de Entrega (delivery note) from an order and layout config.
 * Used by print, send, and the sandbox preview.
 */

import type { Order } from '../../../models/order';
import type {
  DeliveryNoteBlockId,
  DeliveryNoteLayoutConfig,
  DeliveryNoteBlockAlignment,
} from '../../../models/configuration';
import {
  DEFAULT_DELIVERY_NOTE_FONT_FAMILY,
  DEFAULT_DELIVERY_NOTE_TEXT_COLOR,
  DEFAULT_DELIVERY_NOTE_BASE_FONT_SIZE_PX,
} from '../../../models/configuration';
import type { VesFiscalData } from '../../../models/organization';
import { formatExchangeRate } from '../../../utils/formatExchangeRate';

function escapeHtml(value: string | undefined | null): string {
  if (value == null || typeof value !== 'string') return '';
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

export interface DeliveryNoteHtmlOptions {
  showAmounts: boolean;
  amountCurrencyMode: 'referenceCurrency' | 'organizationCurrency' | 'both';
  /**
   * When false, only hides the Desc. % column visually; Precio/Total siguen calculados con descuento.
   * Default true.
   */
  showLineDiscountColumn?: boolean;
}

export interface DeliveryNoteHtmlContext {
  logoUrl: string;
  logoAlt: string;
  referenceCurrency: string;
  organizationCurrency: string;
  formatMoney: (value: number, code: string) => string;
  formatNumber: (value: number) => string;
  /** Organization fiscal data (e.g. VES) – used for fiscal_* blocks when present */
  fiscalData?: VesFiscalData | null;
}

function getSaleReference(order: Order | null | undefined): string {
  if (!order) return '#';
  if (order.saleNumber != null) return `#${order.saleNumber}`;
  if (order.id && typeof order.id === 'string') return `#${order.id.substring(0, 8)}`;
  return '#';
}

function getDisplayIvaAmount(order: Order | null | undefined): number {
  if (!order) return 0;
  const subtotal = Number(order.subtotal) || 0;
  const total = Number(order.total) || 0;
  const stored = Number(order.ivaAmount) || 0;
  return Math.abs(subtotal + stored - total) > 0.01 ? total - subtotal : stored;
}

const VALID_BLOCK_IDS = new Set<DeliveryNoteBlockId>([
  'title', 'logo', 'sale_number', 'client', 'salesperson', 'date', 'delivery_address',
  'observations', 'products_table', 'totals', 'totals_breakdown', 'exchange_hint',
  'fiscal_rif', 'fiscal_taxpayer_type', 'fiscal_withholding_agent', 'fiscal_address',
  'fiscal_state', 'fiscal_municipality',
]);

/**
 * When true, output is optimized for email: table-based totals (reliable column alignment),
 * inline styles for alignment, no flexbox in totals. Use for delivery note emails.
 */
export function buildDeliveryNoteHtml(
  order: Order | null | undefined,
  options: DeliveryNoteHtmlOptions | null | undefined,
  layout: DeliveryNoteLayoutConfig | null | undefined,
  ctx: DeliveryNoteHtmlContext | null | undefined,
  forEmail?: boolean
): string {
  const safeOrder = order ?? ({} as Order);
  const safeOptions = options ?? {
    showAmounts: false,
    amountCurrencyMode: 'referenceCurrency' as const,
    showLineDiscountColumn: true,
  };
  const safeLayout = layout ?? { blockOrder: [] };
  const safeCtx = ctx ?? ({} as DeliveryNoteHtmlContext);

  const blockOrder = Array.isArray(safeLayout.blockOrder)
    ? safeLayout.blockOrder.filter((id): id is DeliveryNoteBlockId => VALID_BLOCK_IDS.has(id as DeliveryNoteBlockId))
    : [];
  const blockAlignments = safeLayout.blockAlignments && typeof safeLayout.blockAlignments === 'object' ? safeLayout.blockAlignments : {};
  const typography = safeLayout.typography && typeof safeLayout.typography === 'object' ? safeLayout.typography : {};
  const blockFontSizes = safeLayout.blockFontSizes && typeof safeLayout.blockFontSizes === 'object' ? safeLayout.blockFontSizes : {};
  const blockColors = safeLayout.blockColors && typeof safeLayout.blockColors === 'object' ? safeLayout.blockColors : {};
  const titleIncludesNumber = safeLayout.titleIncludesNumber;

  const { showAmounts, amountCurrencyMode } = safeOptions;
  const showDiscountPercentColumn = safeOptions.showLineDiscountColumn !== false;
  const fiscal = safeCtx.fiscalData;
  const orderRate = Number(safeOrder.exchangeRate) || 1;
  const fontFamily = (typography?.fontFamily && String(typography.fontFamily).trim()) || DEFAULT_DELIVERY_NOTE_FONT_FAMILY;
  const textColor = (typography?.textColor && String(typography.textColor).trim()) || DEFAULT_DELIVERY_NOTE_TEXT_COLOR;
  const baseFontSizePx = typeof typography?.baseFontSizePx === 'number' && typography.baseFontSizePx > 0 ? typography.baseFontSizePx : DEFAULT_DELIVERY_NOTE_BASE_FONT_SIZE_PX;
  const getAlign = (id: DeliveryNoteBlockId): DeliveryNoteBlockAlignment =>
    (blockAlignments[id] === 'center' || blockAlignments[id] === 'right') ? blockAlignments[id]! : 'left';
  const getBlockFontSize = (id: DeliveryNoteBlockId): number =>
    (typeof blockFontSizes[id] === 'number' && blockFontSizes[id]! > 0) ? blockFontSizes[id]! : baseFontSizePx;
  const getBlockColor = (id: DeliveryNoteBlockId): string =>
    (blockColors[id] && String(blockColors[id]).trim()) || textColor;
  const formatAmount = (value: number, code: string) =>
    typeof safeCtx.formatMoney === 'function' ? safeCtx.formatMoney(value, code) : String(Number(value).toFixed(2));
  const formatNumber = (value: number) =>
    typeof safeCtx.formatNumber === 'function'
      ? safeCtx.formatNumber(value)
      : formatExchangeRate(value);
  const refCurrency = (safeCtx.referenceCurrency && String(safeCtx.referenceCurrency).trim()) || 'USD';
  const orgCurrency = (safeCtx.organizationCurrency && String(safeCtx.organizationCurrency).trim()) || 'VES';
  const formatAmountCell = (value: number): string => {
    const num = Number(value) || 0;
    if (amountCurrencyMode === 'organizationCurrency') {
      return escapeHtml(formatAmount(num * orderRate, orgCurrency));
    }
    if (amountCurrencyMode === 'both') {
      return `${escapeHtml(formatAmount(num, refCurrency))}<br/><small style="color:#666">${escapeHtml(
        formatAmount(num * orderRate, orgCurrency)
      )}</small>`;
    }
    return escapeHtml(formatAmount(num, refCurrency));
  };

  const created = safeOrder.created as { toDate?: () => Date } | Date | undefined;
  let createdText = '';
  if (created != null) {
    try {
      const createdDate =
        created && typeof (created as { toDate?: () => Date }).toDate === 'function'
          ? (created as { toDate: () => Date }).toDate()
          : new Date(created as Date);
      if (!isNaN(createdDate.getTime())) createdText = createdDate.toLocaleString('es-ES');
    } catch {
      createdText = '';
    }
  }

  const products = Array.isArray(safeOrder.products) ? safeOrder.products : [];
  const orderDiscountPercent = Math.min(
    100,
    Math.max(
      0,
      Number(
        (safeOrder as { orderDiscountPercent?: number | null })
          .orderDiscountPercent
      ) || 0
    )
  );
  const orderDiscountFactor = 1 - orderDiscountPercent / 100;
  const tdNumAttr = forEmail ? ' style="border:1px solid #ddd;padding:4px 6px;text-align:right"' : ' class="td-num"';
  const rows = products
    .map((p) => {
      const price = Number(p?.price) || 0;
      const qty = Number(p?.quantity) || 0;
      const discount = Math.min(100, Math.max(0, Number((p as { discount?: number })?.discount) || 0));
      const discountFactor = 1 - discount / 100;
      const lineNet = price * qty * discountFactor * orderDiscountFactor;
      const code = (p?.code != null && String(p.code).trim()) ? String(p.code).trim() : '-';
      const name = (p?.name != null && String(p.name).trim()) ? String(p.name).trim() : '-';
      const discountCell = showDiscountPercentColumn
        ? `<td${tdNumAttr}>${discount.toFixed(2)}%</td>`
        : '';
      return `
        <tr>
          <td${forEmail ? ' style="border:1px solid #ddd;padding:4px 6px"' : ''}>${escapeHtml(code)}</td>
          <td${forEmail ? ' style="border:1px solid #ddd;padding:4px 6px"' : ''}>${escapeHtml(name)}</td>
          <td${tdNumAttr}>${qty}</td>
          ${discountCell}
          ${showAmounts ? `<td${tdNumAttr}>${formatAmountCell(price)}</td>` : ''}
          ${showAmounts ? `<td${tdNumAttr}>${formatAmountCell(lineNet)}</td>` : ''}
        </tr>
      `;
    })
    .join('');

  const subtotal = Number(safeOrder.subtotal) || 0;
  const iva = getDisplayIvaAmount(safeOrder);
  const total = Number(safeOrder.total) || 0;
  const saleRef = getSaleReference(safeOrder);
  const titleText =
    titleIncludesNumber !== false
      ? `Nota de entrega ${saleRef}`
      : 'Nota de Entrega';

  const defaultLogoMaxWidth = 200;
  const defaultLogoMaxHeight = 70;

  const fragments: Record<DeliveryNoteBlockId, string> = {
    title: `<h1>${escapeHtml(titleText)}</h1>`,
    logo: '', // built per-block with size in bodyParts
    sale_number: `<div class="meta"><strong>Venta:</strong> ${escapeHtml(saleRef)}</div>`,
    client: `<div class="meta"><strong>Cliente:</strong> ${escapeHtml(safeOrder.clientName) || '-'}</div>`,
    salesperson: `<div class="meta"><strong>Vendedor:</strong> ${escapeHtml(safeOrder.creatorSalesPerson) || '-'}</div>`,
    date: `<div class="meta"><strong>Fecha:</strong> ${escapeHtml(createdText) || '-'}</div>`,
    delivery_address: `<div class="meta"><strong>Dirección de Entrega:</strong> ${escapeHtml(safeOrder.deliveryAddress) || '-'}</div>`,
    observations:
      (safeOrder.comment && String(safeOrder.comment).trim())
        ? `<div class="meta observations"><strong>Observaciones:</strong> ${escapeHtml(String(safeOrder.comment).trim())}</div>`
        : '',
    products_table: forEmail
      ? `
      <table style="width:100%;border-collapse:collapse;margin-top:10px;font-size:inherit">
        <thead>
          <tr>
            <th style="border:1px solid #ddd;padding:4px 6px;background:#f7f7f7;text-align:left">Código</th>
            <th style="border:1px solid #ddd;padding:4px 6px;background:#f7f7f7;text-align:left">Producto</th>
            <th style="border:1px solid #ddd;padding:4px 6px;background:#f7f7f7;text-align:right">Cantidad</th>
            ${showDiscountPercentColumn ? '<th style="border:1px solid #ddd;padding:4px 6px;background:#f7f7f7;text-align:right">Desc. %</th>' : ''}
            ${showAmounts ? '<th style="border:1px solid #ddd;padding:4px 6px;background:#f7f7f7;text-align:right">Precio</th><th style="border:1px solid #ddd;padding:4px 6px;background:#f7f7f7;text-align:right">Total</th>' : ''}
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`
      : `
      <table>
        <thead>
          <tr>
            <th>Código</th>
            <th>Producto</th>
            <th class="th-num">Cantidad</th>
            ${showDiscountPercentColumn ? '<th class="th-num">Desc. %</th>' : ''}
            ${showAmounts ? '<th class="th-num">Precio</th><th class="th-num">Total</th>' : ''}
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`,
    totals: showAmounts
      ? forEmail
        ? `<table style="margin-top:10px;width:320px;margin-left:auto;border-collapse:collapse;font-size:inherit" cellpadding="0" cellspacing="0">
          ${orderDiscountPercent > 0 ? `<tr><td style="padding:3px 12px 3px 0;vertical-align:top"><strong>Descuento general:</strong></td><td style="width:180px;min-width:180px;text-align:right;padding:3px 0;vertical-align:top">${orderDiscountPercent.toFixed(2)}%</td></tr>` : ''}
          <tr><td style="padding:3px 12px 3px 0;vertical-align:top"><strong>Subtotal:</strong></td><td style="width:180px;min-width:180px;text-align:right;padding:3px 0;vertical-align:top">${formatAmountCell(subtotal)}</td></tr>
          <tr><td style="padding:3px 12px 3px 0;vertical-align:top"><strong>Impuestos:</strong></td><td style="width:180px;min-width:180px;text-align:right;padding:3px 0;vertical-align:top">${formatAmountCell(iva)}</td></tr>
          <tr><td style="padding:3px 12px 3px 0;vertical-align:top"><strong>Total:</strong></td><td style="width:180px;min-width:180px;text-align:right;padding:3px 0;vertical-align:top">${formatAmountCell(total)}</td></tr>
        </table>`
        : `<div class="totals">
          ${orderDiscountPercent > 0 ? `<div class="totals-row"><strong>Descuento general:</strong><span class="totals-amount">${orderDiscountPercent.toFixed(2)}%</span></div>` : ''}
          <div class="totals-row"><strong>Subtotal:</strong><span class="totals-amount">${formatAmountCell(subtotal)}</span></div>
          <div class="totals-row"><strong>Impuestos:</strong><span class="totals-amount">${formatAmountCell(iva)}</span></div>
          <div class="totals-row"><strong>Total:</strong><span class="totals-amount">${formatAmountCell(total)}</span></div>
        </div>`
      : '',
    totals_breakdown: showAmounts
      ? (() => {
          let exento = 0;
          let baseImponible = 0;
          for (const p of products) {
            const discount = Math.min(100, Math.max(0, Number((p as { discount?: number })?.discount) || 0));
            const lineTotal =
              (Number(p.price) || 0) *
              (Number(p.quantity) || 0) *
              (1 - discount / 100) *
              orderDiscountFactor;
            if (Number(p.taxRate) === 0) {
              exento += lineTotal;
            } else {
              baseImponible += lineTotal;
            }
          }
          return forEmail
            ? `<table style="margin-top:10px;width:320px;margin-left:auto;border-collapse:collapse;font-size:inherit" cellpadding="0" cellspacing="0">
          <tr><td style="padding:3px 12px 3px 0;vertical-align:top"><strong>Exento:</strong></td><td style="width:180px;min-width:180px;text-align:right;padding:3px 0;vertical-align:top">${formatAmountCell(exento)}</td></tr>
          <tr><td style="padding:3px 12px 3px 0;vertical-align:top"><strong>Base imponible:</strong></td><td style="width:180px;min-width:180px;text-align:right;padding:3px 0;vertical-align:top">${formatAmountCell(baseImponible)}</td></tr>
          <tr><td style="padding:3px 12px 3px 0;vertical-align:top"><strong>IVA:</strong></td><td style="width:180px;min-width:180px;text-align:right;padding:3px 0;vertical-align:top">${formatAmountCell(iva)}</td></tr>
          <tr><td style="padding:3px 12px 3px 0;vertical-align:top"><strong>Total:</strong></td><td style="width:180px;min-width:180px;text-align:right;padding:3px 0;vertical-align:top">${formatAmountCell(total)}</td></tr>
        </table>`
            : `<div class="totals">
          <div class="totals-row"><strong>Exento:</strong><span class="totals-amount">${formatAmountCell(exento)}</span></div>
          <div class="totals-row"><strong>Base imponible:</strong><span class="totals-amount">${formatAmountCell(baseImponible)}</span></div>
          <div class="totals-row"><strong>IVA:</strong><span class="totals-amount">${formatAmountCell(iva)}</span></div>
          <div class="totals-row"><strong>Total:</strong><span class="totals-amount">${formatAmountCell(total)}</span></div>
        </div>`;
        })()
      : '',
    exchange_hint:
      showAmounts && amountCurrencyMode === 'both'
        ? `<div class="hint">Tasa de conversion aplicada: 1 ${escapeHtml(refCurrency)} = ${escapeHtml(formatNumber(orderRate))} ${escapeHtml(orgCurrency)}</div>`
        : '',
    fiscal_rif:
      fiscal?.rif?.trim() ?
        `<div class="meta"><strong>R.I.F.:</strong> ${escapeHtml(fiscal.rif.trim())}</div>` : '',
    fiscal_taxpayer_type:
      fiscal?.taxpayerType ?
        `<div class="meta"><strong>Tipo de contribuyente:</strong> ${escapeHtml(fiscal.taxpayerType)}</div>` : '',
    fiscal_withholding_agent:
      fiscal && typeof fiscal.withholdingAgent === 'boolean'
        ? `<div class="meta"><strong>Agente de retención:</strong> ${fiscal.withholdingAgent ? 'Sí' : 'No'}</div>`
        : '',
    fiscal_address:
      fiscal?.fiscalAddress?.trim() ?
        `<div class="meta"><strong>Dirección fiscal:</strong> ${escapeHtml(fiscal.fiscalAddress.trim())}</div>` : '',
    fiscal_state:
      fiscal?.fiscalState?.trim() ?
        `<div class="meta"><strong>Estado:</strong> ${escapeHtml(fiscal.fiscalState.trim())}</div>` : '',
    fiscal_municipality:
      fiscal?.fiscalMunicipality?.trim() ?
        `<div class="meta"><strong>Municipio:</strong> ${escapeHtml(fiscal.fiscalMunicipality.trim())}</div>` : '',
  };

  const justifyContent =
    (align: DeliveryNoteBlockAlignment) =>
    (align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start');

  const bodyParts = blockOrder
    .map((id) => {
      const blockId = id as DeliveryNoteBlockId;
      const isLogo = id === 'logo';
      let html = fragments[blockId];
      if (isLogo) {
        const logoSizePx = blockFontSizes['logo'];
        const maxW = logoSizePx ?? defaultLogoMaxWidth;
        const maxH = logoSizePx ?? defaultLogoMaxHeight;
        const logoUrl = safeCtx.logoUrl && String(safeCtx.logoUrl).trim();
        const logoWrapStyle = forEmail
          ? `display:inline-block;max-width:${maxW}px;max-height:${maxH}px`
          : `max-width:${maxW}px;max-height:${maxH}px`;
        html =
          logoUrl
            ? `<div class="delivery-logo-wrap" style="${logoWrapStyle}"><img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(safeCtx.logoAlt)}" class="delivery-logo" style="max-width:${maxW}px;max-height:${maxH}px;width:auto;height:auto;object-fit:contain;display:block" /></div>`
            : '';
      }
      if (!html) return '';
      const align = getAlign(blockId);
      const fontSize = getBlockFontSize(blockId);
      const color = getBlockColor(blockId);
      const baseStyle = isLogo
        ? forEmail
          ? `text-align:${align}`
          : `display:flex;justify-content:${justifyContent(align)}`
        : `text-align:${align}`;
      const typoStyle = isLogo
        ? `color:${color}`
        : `font-size:${fontSize}px;color:${color}`;
      const style = `${baseStyle};${typoStyle}`;
      return `<div class="delivery-block" style="${style}">${html}</div>`;
    })
    .filter(Boolean);

  const bodyHtml = bodyParts.join('\n');

  // Email clients often ignore <style> in head; put typography inline on body (and wrapper) so chosen font is applied
  const bodyInlineStyle = forEmail
    ? `font-family:${escapeHtml(fontFamily)};padding:16px;color:${escapeHtml(textColor)};font-size:${baseFontSizePx}px`
    : '';

  const bodyContent = forEmail && bodyInlineStyle
    ? `<div style="${bodyInlineStyle}">${bodyHtml}</div>`
    : bodyHtml;

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Nota de Entrega ${safeOrder.saleNumber != null ? `#${safeOrder.saleNumber}` : ''}</title>
  <style>
    body { font-family: ${escapeHtml(fontFamily)}; padding: 16px; color: ${escapeHtml(textColor)}; font-size: ${baseFontSizePx}px; }
    h1 { margin-bottom: 2px; }
    .meta { margin: 2px 0; font-size: inherit; }
    .observations { margin-top: 6px; padding: 4px 0; border-top: 1px solid #eee; }
    .header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 6px; }
    .header-logo, .delivery-logo-wrap { max-width: 200px; max-height: 70px; display: inline-block; overflow: visible; }
    .delivery-logo { max-width: 200px; max-height: 70px; width: auto; height: auto; object-fit: contain; object-position: center; display: block; vertical-align: middle; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: inherit; }
    th, td { border: 1px solid #ddd; padding: 4px 6px; }
    th { background: #f7f7f7; text-align: left; }
    th.th-num { text-align: right; }
    td.td-num { text-align: right; }
    .totals { margin-top: 10px; width: 320px; margin-left: auto; font-size: inherit; }
    .totals-row { display: flex; justify-content: space-between; align-items: baseline; margin: 3px 0; gap: 12px; }
    .totals-row .totals-amount { text-align: right; width: 160px; min-width: 160px; flex-shrink: 0; display: block; }
    .hint { color: inherit; font-size: 0.833em; margin-top: 8px; }
    .delivery-block { margin: 2px 0; }
  </style>
</head>
<body${bodyInlineStyle ? ` style="${bodyInlineStyle}"` : ''}>
${bodyContent}
</body>
</html>`;
}
